import React, { useState, useRef, useEffect } from "react";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { Dialog, Button, Image } from "hesus-ui-kit";
import { renderPriceWithCurrency, addSpace } from "@utils/utils";
import closeIcon from "@icons/ic-close-white.svg";
import RealisedQuantityRow from "./RealisedQuantityRow";
import { getDateFormat } from "@utils/datePickerUtils";
import {
  PopupRoot,
  HeaderWrapper,
  Title,
  ButtonWrapper,
  FormBody,
  FlexRow,
  SectionContainer,
  CaptionColor,
  CaptionWrapper,
  SummaryRows,
  SummaryHeader,
} from "./RealisedQuantityPopup.styled";
import { GridRow, GridCell } from "../../AddEditInvoice.styled";

const RealisedQuantityPopup = (props) => {
  const {
    t,
    quoteItem,
    onClose,
    quoteService,
    startDate,
    endDate,
    onSummarySelection,
  } = props;

  const summaries = quoteItem?.realisedQuantityData?.summaries || [];

  const [localSummaries, setLocalSummaries] = useState(summaries);
  const [scrollbarWidth, setScrollbarWidth] = useState(0);
  const hesusService = quoteItem.hesusService || {};
  const {
    needSupplierPlanning,
    needCombination,
    needCenter,
    needCarrier,
    needCombinationCarrier,
    needCombinationCenter,
  } = hesusService;

  const showCentre =
    needSupplierPlanning &&
    needCenter &&
    (!needCombination || (needCombination && needCombinationCarrier));

  const showHaulier =
    needSupplierPlanning &&
    needCarrier &&
    (!needCombination || (needCombination && needCombinationCenter));

  const getRealisedSummary = (summary) => {
    if (!needSupplierPlanning) {
      return summary?.otherServiceSummary;
    }
    if (showCentre && !showHaulier) {
      return summary?.supplierPlanningLineSummary?.centreSummary;
    }
    if (showHaulier && !showCentre) {
      return summary?.supplierPlanningLineSummary?.haulierSummary;
    }
    if (showCentre && showHaulier) {
      return quoteItem.unit === "LOAD"
        ? summary?.supplierPlanningLineSummary?.haulierSummary
        : summary?.supplierPlanningLineSummary?.centreSummary;
    }
    return null;
  };

  const getIsInvoiced = (summary) => {
    const unit = quoteItem.unit;

    return !needSupplierPlanning
      ? summary.otherServiceSummary?.invoiced === true
      : needCenter && needCarrier && !needCombination
      ? unit === "LOAD"
        ? summary.supplierPlanningLineSummary?.haulierSummary?.invoiced === true
        : ["M3", "TON"].includes(unit)
        ? summary.supplierPlanningLineSummary?.centreSummary?.invoiced === true
        : false
      : needCenter &&
        (!needCombination || (needCombination && needCombinationCarrier))
      ? summary.supplierPlanningLineSummary?.centreSummary?.invoiced === true
      : needCarrier &&
        (!needCombination || (needCombination && needCombinationCenter))
      ? summary.supplierPlanningLineSummary?.haulierSummary?.invoiced === true
      : false;
  };

  const selectableSummaries = localSummaries.filter((summary) => {
    const realisedSummary = getRealisedSummary(summary);
    return !getIsInvoiced(summary) && !!realisedSummary;
  });

  const allSelected =
    selectableSummaries.length > 0 &&
    selectableSummaries.every((summary) => summary.selected);

  const handleSummaryChange = (realisedQuantityId) => {
    setLocalSummaries((prev) =>
      prev.map((summary) =>
        summary.realisedQuantityId === realisedQuantityId
          ? {
              ...summary,
              selected: !summary.selected,
            }
          : summary
      )
    );
  };
  const handleSelectAll = () => {
    const shouldSelectAll = !allSelected;

    setLocalSummaries((prev) =>
      prev.map((summary) => {
        const realisedSummary = getRealisedSummary(summary);
        const isInvoiced = getIsInvoiced(summary);

        if (isInvoiced || !realisedSummary) {
          return summary;
        }

        return {
          ...summary,
          selected: shouldSelectAll,
        };
      })
    );
  };

  const totalQuantity = localSummaries
    .reduce((total, summary) => {
      const realisedSummary = getRealisedSummary(summary);
      if (getIsInvoiced(summary) || !realisedSummary || !summary.selected) {
        return total;
      }
      return total + Number(realisedSummary.quantity || 0);
    }, 0)
    .toFixed(2);

  const handleValidate = () => {
    onSummarySelection({
      summaries: localSummaries,
      quantityRealised: totalQuantity,
    });

    onClose();
  };

  const captionItems = [
    {
      color: "#E1FFDE",
      label: t("CLIENT_INVOICING_SUMMARY_CAPTION_READY_INVOICING"),
    },
    {
      color: "#FFEABC",
      label: t("CLIENT_INVOICING_SUMMARY_CAPTION_IN_PROGRESS"),
    },
    {
      color: "#FFC8BD",
      label: t("CLIENT_INVOICING_SUMMARY_CAPTION_NOT_STARTED"),
    },
    { color: "#EDEDED", label: t("CLIENT_INVOICING_SUMMARY_CAPTION_INVOICED") },
  ];

  const gridColumns = [
    "5%",
    "10%",
    "15%",
    showHaulier && showCentre
      ? "29%"
      : showHaulier || showCentre
      ? "58%"
      : null,
    showCentre && showHaulier ? "29%" : showCentre || showHaulier ? null : null,
    "12%",
  ]
    .filter(Boolean)
    .join(" ");

  const summaryRowsRef = useRef(null);

  useEffect(() => {
    const rowsElement = summaryRowsRef.current;

    if (!rowsElement) return;

    const updateScrollbarWidth = () => {
      setScrollbarWidth(rowsElement.offsetWidth - rowsElement.clientWidth);
    };

    updateScrollbarWidth();

    const observer = new ResizeObserver(updateScrollbarWidth);
    observer.observe(rowsElement);

    return () => observer.disconnect();
  }, []);

  return (
    <PopupRoot>
      <Dialog width="70rem">
        <div>
          <HeaderWrapper>
            <Title>{t("INVOICES_SUMMARIES_SELECTION_TITLE")}</Title>
            <Button
              onClick={onClose}
              styles={{
                backgroundColor: "transparent",
                border: "none",
                height: "auto",
                padding: 0,
                "&:hover": {
                  backgroundColor: "transparent",
                },
              }}
            >
              <Image
                src={closeIcon}
                alt={t("CLOSE")}
                styles={{ width: "auto" }}
              />
            </Button>
          </HeaderWrapper>
          <FormBody>
            <FlexRow>
              <SectionContainer>
                <div>
                  <GridRow margin="0 0 10px 0" padding="0px">
                    <GridCell fontWeight="700" color="#0A4986" fontSize="16px">
                      {quoteItem.quoteItemLabel} (
                      {renderPriceWithCurrency(
                        addSpace(quoteItem.sellingPrice),
                        t(quoteItem.sellingUnit),
                        quoteService.currency.name,
                        quoteService.currency.position
                      )}
                      )
                      {(hesusService.needProduct ||
                        hesusService.needTransport) &&
                        " - "}
                      {hesusService.needProduct && (
                        <span
                          style={{
                            fontWeight: 400,
                            color: "#474747",
                            fontStyle: "italic",
                          }}
                        >
                          {quoteItem.product.name}
                        </span>
                      )}
                      {hesusService.needProduct && hesusService.needTransport && (
                        <span
                          style={{
                            fontWeight: 400,
                            color: "#474747",
                            fontStyle: "italic",
                          }}
                        >
                          {" / "}
                        </span>
                      )}
                      {hesusService.needTransport && (
                        <span
                          style={{
                            fontWeight: 400,
                            color: "#474747",
                            fontStyle: "italic",
                          }}
                        >
                          {quoteItem.transport.name}
                        </span>
                      )}
                    </GridCell>
                  </GridRow>
                  <GridRow padding="0px" margin="0 0 10px 0">
                    <GridCell color="#00AEEA" fontWeight="700" fontSize="15px">
                      {t("INVOICES_SUMMARIES_SELECTION_FROM") +
                        " " +
                        getDateFormat(startDate, "YYYY-MM-DD") +
                        " " +
                        t("INVOICES_SUMMARIES_SELECTION_TO") +
                        " " +
                        getDateFormat(endDate, "YYYY-MM-DD")}{" "}
                    </GridCell>
                  </GridRow>

                  <GridRow padding="0px" gap="0px">
                    {/* Header */}
                    <SummaryHeader
                      style={{
                        paddingRight: `${scrollbarWidth}px`,
                        boxSizing: "border-box",
                      }}
                    >
                      <GridRow
                        column={gridColumns}
                        alignItems="center"
                        background="#008395"
                        borderRadius="5px 5px 0 0"
                        padding="0"
                        gap="0px"
                      >
                        <GridCell
                          color="#fff"
                          fontWeight="700"
                          fontSize="14px"
                          textAlign="center"
                          padding="8px"
                        >
                          <input
                            type="checkbox"
                            checked={allSelected}
                            onChange={handleSelectAll}
                            disabled={!selectableSummaries.length}
                          />
                        </GridCell>

                        <GridCell
                          color="#fff"
                          fontWeight="700"
                          fontSize="14px"
                          textAlign="center"
                          padding="8px"
                          borderLeft="1px solid #fff"
                        >
                          {t("INVOICES_SUMMARIES_SELECTION_DATE")}
                        </GridCell>

                        <GridCell
                          color="#fff"
                          fontWeight="700"
                          fontSize="14px"
                          textAlign="center"
                          padding="8px"
                          borderLeft="1px solid #fff"
                        >
                          {t("INVOICES_SUMMARIES_SELECTION_PLANNED")}
                        </GridCell>

                        {showHaulier && (
                          <GridCell
                            color="#fff"
                            fontWeight="700"
                            fontSize="14px"
                            textAlign="center"
                            padding="8px"
                            borderLeft="1px solid #fff"
                          >
                            {t("INVOICES_SUMMARIES_SELECTION_HAULIER")}
                          </GridCell>
                        )}

                        {showCentre && (
                          <GridCell
                            color="#fff"
                            fontWeight="700"
                            fontSize="14px"
                            textAlign="center"
                            padding="8px"
                            borderLeft="1px solid #fff"
                          >
                            {t("INVOICES_SUMMARIES_SELECTION_CENTRE")}
                          </GridCell>
                        )}

                        <GridCell
                          color="#fff"
                          fontWeight="700"
                          fontSize="14px"
                          textAlign="center"
                          padding="8px"
                          borderLeft="1px solid #fff"
                        >
                          {t("INVOICES_SUMMARIES_SELECTION_REALISED")}
                        </GridCell>
                      </GridRow>
                    </SummaryHeader>
                    {/* Rows */}
                    <SummaryRows ref={summaryRowsRef}>
                      {localSummaries.map((summary) => (
                        <RealisedQuantityRow
                          gridColumns={gridColumns}
                          key={summary.realisedQuantityId}
                          summary={summary}
                          quoteItem={quoteItem}
                          showHaulier={showHaulier}
                          showCentre={showCentre}
                          onChange={handleSummaryChange}
                        />
                      ))}
                    </SummaryRows>
                    {/* Total */}
                    <SummaryHeader
                      style={{
                        paddingRight: `${scrollbarWidth}px`,
                        boxSizing: "border-box",
                      }}
                    >
                      <GridRow
                        column="1fr 12%"
                        alignItems="center"
                        background="#0A4986"
                        borderRadius="0px 0px 5px 5px"
                        padding="0"
                        gap="0px"
                      >
                        <GridCell
                          color="#fff"
                          fontWeight="700"
                          fontSize="14px"
                          textAlign="left"
                          padding="8px"
                        >
                          {t("CLIENT_INVOICING_SUMMARY_SELECTION_TOTAL")}
                        </GridCell>
                        <GridCell
                          color="#fff"
                          fontWeight="700"
                          fontSize="14px"
                          textAlign="center"
                          padding="8px"
                          borderLeft="1px solid #fff"
                        >
                          {totalQuantity} {t(quoteItem.quoteItemUnit)}
                        </GridCell>
                      </GridRow>
                    </SummaryHeader>
                  </GridRow>
                  <GridRow column="repeat(4, auto)" gap="10px" padding="0">
                    {captionItems.map(({ color, label }) => (
                      <GridCell
                        key={label}
                        color="#474747"
                        fontSize="14px"
                        padding="8px"
                        fontStyle="italic"
                      >
                        <CaptionWrapper>
                          <CaptionColor color={color} />
                          <span>{label}</span>
                        </CaptionWrapper>
                      </GridCell>
                    ))}
                  </GridRow>
                </div>
              </SectionContainer>
            </FlexRow>
          </FormBody>

          <ButtonWrapper>
            <Button
              styles={{
                color: "#333",
                background: "#fff",
                marginRight: "0.32rem",
                border: "1px solid #ccc",
                "&:hover": {
                  backgroundColor: "#ccc",
                },
              }}
              onClick={onClose}
            >
              {t("CANCEL")}
            </Button>
            <Button
              styles={{
                color: "white",
                background: "#1BACE4",
                border: "1px solid #1BACE4",
                "&:hover": {
                  backgroundColor: "#1589b6",
                },
              }}
              onClick={handleValidate}
            >
              {t("VALIDATE")}
            </Button>
          </ButtonWrapper>
        </div>
      </Dialog>
    </PopupRoot>
  );
};

RealisedQuantityPopup.propTypes = {
  t: PropTypes.func.isRequired,
  onSummarySelection: PropTypes.func.isRequired,
};

RealisedQuantityPopup.defaultProps = {};
export default withTranslation()(RealisedQuantityPopup);
